import { Heart, Plus, Pencil } from "lucide-react";
import { RISK_STYLES, ROLE_LABELS, fmt, Vital } from "@/lib/clinical";

export default function ObservationsSection({ observations, onNew, onEdit }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold text-[#0F2547] flex items-center gap-2">
          <Heart className="h-5 w-5 text-[#D5281B]" /> Observations Timeline
        </h2>
        <button onClick={onNew} data-testid="new-observation-button"
          className="inline-flex items-center gap-2 h-12 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-sm font-semibold">
          <Plus className="h-4 w-4" /> New Observation
        </button>
      </div>

      {observations.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center" data-testid="no-observations-message">
          No observations recorded yet. Add the first set of vitals.
        </p>
      ) : (
        <div className="relative space-y-4 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200" data-testid="observations-timeline">
          {observations.map((o) => (
            <div key={o.id} className="relative pl-7" data-testid={`observation-entry-${o.id}`}>
              <div className="absolute left-0 top-2 h-4 w-4 rounded-full border-2 border-[#005EB8] bg-white" />
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-[#F4F7FA] border-b border-slate-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-vitals text-sm font-bold text-[#0F2547]">{fmt(o.created_at)}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${RISK_STYLES[o.risk]}`}>
                      NEWS2 {o.news2}
                    </span>
                  </div>
                  <button onClick={() => onEdit(o)} data-testid={`edit-observation-${o.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#005EB8] hover:underline min-h-[36px]">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                </div>
                <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Vital label="Heart Rate" value={o.heart_rate} unit="bpm" />
                  <Vital label="Blood Pressure" value={o.bp_systolic != null ? `${o.bp_systolic}/${o.bp_diastolic ?? "—"}` : null} unit="mmHg" />
                  <Vital label="Resp Rate" value={o.respiratory_rate} unit="/min" />
                  <Vital label="SpO2" value={o.spo2} unit="%" />
                  <Vital label="Temp" value={o.temperature} unit="°C" />
                  <Vital label="Glucose" value={o.blood_glucose} unit="mmol/L" />
                  {(o.pupils || o.pupil_left_mm != null || o.pupil_right_mm != null) && (
                    <div className="bg-[#0B192C] rounded-lg px-3 py-2.5 col-span-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Pupils</p>
                      <p className="text-sm font-bold text-[#38BDF8]">
                        {[o.pupils, o.pupil_left_mm != null || o.pupil_right_mm != null ? `L ${o.pupil_left_mm ?? "—"}mm · R ${o.pupil_right_mm ?? "—"}mm` : null].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  )}
                  {o.consciousness && (
                    <div className="bg-[#0B192C] rounded-lg px-3 py-2.5 col-span-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Consciousness (AVPU)</p>
                      <p className="text-sm font-bold text-[#38BDF8]">{o.consciousness}</p>
                    </div>
                  )}
                  {(o.gcs_eyes != null || o.gcs_verbal != null || o.gcs_motor != null) && (
                    <div className="bg-[#0B192C] rounded-lg px-3 py-2.5 col-span-2" data-testid={`gcs-display-${o.id}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">GCS</p>
                      <p className="text-sm font-bold text-[#38BDF8]">
                        E{o.gcs_eyes ?? "—"} V{o.gcs_verbal ?? "—"} M{o.gcs_motor ?? "—"}
                        {" · Total "}{[o.gcs_eyes, o.gcs_verbal, o.gcs_motor].filter((v) => v != null).reduce((a, b) => a + b, 0)}/15
                      </p>
                    </div>
                  )}
                </div>
                {o.comment && <p className="px-4 pb-3 text-sm text-slate-600 italic">"{o.comment}"</p>}
                <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                  <span className="font-semibold text-[#0F2547]">{o.recorded_by_name}</span>
                  <span className="uppercase tracking-wide font-bold text-[#005EB8]">{ROLE_LABELS[o.recorded_by_role] || o.recorded_by_role}</span>
                  {o.edit_history?.length > 0 && (
                    <span className="text-amber-700" data-testid={`observation-edit-history-${o.id}`}>
                      · Last edited by {o.edit_history[o.edit_history.length - 1].edited_by_name} at {fmt(o.edit_history[o.edit_history.length - 1].edited_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
